import "./login.css";
import { useUserNotificationStore } from "src/features/notifications/store";

import * as Sentry from "@sentry/react";
import { resetPostHogUser, setPostHogUser } from "src/config/posthog";

import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardGroup,
  CCol,
  CContainer,
  CForm,
  CImg,
  CInput,
  CInputGroup,
  CInputGroupAppend,
  CInputGroupPrepend,
  CInputGroupText,
  CLink,
  CRow,
} from "@coreui/react";
import { LinearProgress, Button as MuiButton } from "@mui/material";
import {
  OAuthService,
  useOAuth,
  buildOAuthProvider,
} from "src/helpers/OAuth/oauth";
import { QueryClient, useQueryClient } from "react-query";
import WrappedAzureADLogin, {
  useMSALInstance,
} from "src/helpers/AzureAD/azureAD";
import {
  determineLoginPlatformType,
  isFastDraft,
} from "src/reusable/Functions";
import { disable_raw_creds, nuclearn_api_url } from "src/App";
import { useEffect, useRef, useState } from "react";

import CIcon from "@coreui/icons-react";
import GitHubIcon from "@mui/icons-material/GitHub";
import GoogleIcon from "@mui/icons-material/Google";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import JumpcloudIcon from "@mui/icons-material/Cloud";
import OktaIconComponent from "src/reusable/components/OktaLogoComponent";
import { User } from "src/types/userTypes";
import { UserService } from "src/views/users/UserService";
import axios from "axios";
import { extractErrorDetail } from "src/helpers/apiError";
import { isAuthorizedUser } from "src/helpers/Auth";
import { nuclearn_user_token } from "src/App";
import useAuthConfig from "src/helpers/AuthConfig";
import {
  useGetProviders,
  OAuthProviderAPI,
} from "src/views/oauth/OAuthServices";
import { useHistory, useLocation } from "react-router-dom";
import validator from "email-validator"; // eslint-disable-line no-undef
import { regenerateCSRFToken, clearCSRFToken } from "src/helpers/csrf";
import { formatCountdown } from "src/reusable/utils/formatCountdown";
import { EMAIL_MIN_LENGTH, EMAIL_MAX_LENGTH } from "src/constants";

const NoRegistrationMessage = `Contact your ${determineLoginPlatformType()} Administrator to create an account.`;

interface LoginError {
  message: string;
  show: boolean;
}

const LoginError = (props: LoginError) => {
  return (
    <CAlert color="danger" show={props.show} closeButton>
      {props.message}
    </CAlert>
  );
};

interface RegisterNavProps {
  allowUserCreation: boolean;
}

const RegisterNav = ({ allowUserCreation = false }: RegisterNavProps) => {
  if (!allowUserCreation) {
    return <span style={{ color: "#768192" }}>{NoRegistrationMessage}</span>;
  } else {
    return (
      <CRow>
        <CCol xs="6" className="text-left">
          <CLink
            to="/password-reset-request"
            data-attr="auth:link:go-to-password-reset"
          >
            <CButton
              style={{ color: "#768192" }}
              hidden={disable_raw_creds}
              className="px-0"
              variant="ghost"
              data-attr="auth:btn:forgot-password"
            >
              Forgot Password
            </CButton>
          </CLink>
        </CCol>

        <CCol xs="6" className="text-right">
          <CLink to="/register" data-attr="auth:link:go-to-register">
            <CButton
              hidden={disable_raw_creds}
              style={{ color: "#768192" }}
              className="px-0"
              variant="ghost"
              data-attr="auth:btn:create-account"
            >
              Create Account
            </CButton>
          </CLink>
        </CCol>
      </CRow>
    );
  }
};

interface ProviderButtonProps {
  providerKey: string;
  provider: {
    name: string;
    disabled: boolean;
  };
  onLogin: (providerId: string) => void;
}

const ProviderButton = ({
  providerKey,
  provider,
  onLogin,
}: ProviderButtonProps) => {
  const providerIcons: Record<
    "github" | "google" | "jumpcloud" | "okta" | "ping",
    React.ElementType | (() => JSX.Element)
  > = {
    github: GitHubIcon,
    google: GoogleIcon,
    jumpcloud: JumpcloudIcon,
    okta: () => (
      <div className="ml-1">
        <OktaIconComponent />
      </div>
    ),
    ping: () => (
      <div className="ml-1">
        <img height="24" width="24" src="images/oauth/p_id.png" />
      </div>
    ),
  };

  const IconComponent =
    providerIcons[providerKey.toLowerCase() as keyof typeof providerIcons] ||
    null;
  return (
    <MuiButton
      hidden={provider.disabled}
      key={providerKey}
      variant="contained"
      color="primary"
      className="px-4 w-100 mt-2"
      type="button"
      onClick={() => onLogin(providerKey)}
      data-attr={`auth:btn:login-with-${providerKey.toLowerCase()}`}
    >
      {`Login with ${provider.name}`}
      {IconComponent && <IconComponent className="ml-1" />}
    </MuiButton>
  );
};

interface LoginButtonProps {
  disabled: boolean;
  countdown?: number;
}

const LoginButton = (props: LoginButtonProps) => {
  const isCountdownActive =
    props.countdown !== undefined && props.countdown > 0;

  return (
    <CButton
      hidden={disable_raw_creds}
      color="success"
      className="px-4"
      type="submit"
      block
      disabled={props.disabled || isCountdownActive}
      data-testid="login-submit-btn"
      data-attr="auth:btn:login-submit"
    >
      Login
    </CButton>
  );
};

// SSO provider buttons. Rendered outside ``LoginButton`` so they stay
// visible when the password form is hidden — both via the static
// ``DISABLE_RAW_CREDS`` deployment flag and the per-provider
// ``disable_local_login`` kill switch. Hiding these alongside the
// password form would defeat the kill switch (no way to sign in).
const ProviderButtons = () => {
  const { login } = useOAuth();
  const { data: oauthProviders } = useGetProviders();

  const handleLogin = async (providerId: string) => {
    try {
      if (!oauthProviders) {
        throw new Error("OAuth providers are not available.");
      }

      const provider = Object.values(oauthProviders).find(
        (p) => p.name === providerId,
      ) as OAuthProviderAPI | undefined;

      if (!provider) {
        throw new Error(`Provider with id ${providerId} not found`);
      }

      // Enrich the raw API provider into a full UserManager config
      // (disablePKCE:true + MRE token_endpoint) and drive the redirect
      // from the resulting OAuthService directly. The context ``login()``
      // resolves the provider from a map built in a separate effect,
      // which may not be populated yet when the single-provider
      // auto-redirect fires right after /list-providers resolves — that
      // would throw "Unknown provider" and the redirect would silently
      // fail. Fall back to the context login if the direct call fails
      // before navigation.
      const service = await OAuthService.reinitializePKCEManager(
        buildOAuthProvider(provider),
      );
      try {
        await service.login();
      } catch (directError) {
        console.warn(
          `${providerId} direct login failed, falling back to context login`,
          directError,
        );
        await login(providerId);
      }
    } catch (error) {
      console.error(`${providerId} login failed:`, error);
    }
  };

  // Auto-redirect to SSO when the password form is hidden and exactly
  // one provider exists — the lone "Sign in with X" button is a
  // dead-end extra click. Skipped during an OAuth callback return
  // (``?code=``/``?error=``) so the callback completes and an IdP error
  // stays visible instead of bouncing straight back to the IdP. Guarded
  // by a ref so React double-invokes / re-renders can't fire it twice.
  const autoRedirected = useRef(false);
  useEffect(() => {
    if (autoRedirected.current || !oauthProviders) {
      return;
    }
    const providers = Object.values(oauthProviders);
    const passwordHidden =
      disable_raw_creds ||
      providers.some((p: OAuthProviderAPI) => p.disable_local_login);
    const params = new URLSearchParams(window.location.search);
    if (
      providers.length !== 1 ||
      !passwordHidden ||
      params.has("code") ||
      params.has("error")
    ) {
      return;
    }
    autoRedirected.current = true;
    void handleLogin(providers[0].name);
  }, [oauthProviders]);

  if (!oauthProviders) {
    return null;
  }

  return (
    <>
      {Object.entries(oauthProviders).map(([, provider]) => (
        <ProviderButton
          key={provider?.name}
          providerKey={provider?.name}
          provider={provider}
          onLogin={handleLogin}
        />
      ))}
    </>
  );
};

function getUserFromToken(): Promise<User> {
  // authorization header set by interceptor
  return axios
    .get(nuclearn_api_url + "/user/")
    .then((response) => {
      return response.data;
    })
    .catch(function (error) {
      console.log(error);
      // set error message
      throw new Error(error);
    });
}

interface RawCredsLoginProps {
  setAuthorized: (isAuthorized: boolean) => void;
  error: { show: boolean; message: string; error: any };
  setError: (error: { show: boolean; message: string; error: string }) => void;
  allowUserCreation: boolean;
}

const RawCredsLogin = (props: RawCredsLoginProps) => {
  const [user, setUser] = useState({ username: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [countdown, setCountdown] = useState<number>(0);
  const [countdownMessage, setCountdownMessage] = useState<string>("");
  const [emailForResend, setEmailForResend] = useState<string | null>(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const { data: oauthProviders } = useGetProviders();
  const history = useHistory();

  // True when an admin has flipped ``disable_local_login`` on any
  // provider returned by ``/list-providers``. The backend already
  // filters admin-disabled providers out of that list, so anything
  // we see here is intended to be available — we only need to check
  // the kill-switch flag itself. The backend will also refuse
  // password login in this state (with code ``LOCAL_LOGIN_DISABLED``);
  // we hide the form to keep the UI honest. ``disable_raw_creds``
  // remains a separate static deployment flag for installs without
  // any SSO.
  const localLoginDisabledByProvider =
    oauthProviders?.some((p: OAuthProviderAPI) => p.disable_local_login) ??
    false;
  const passwordLoginHidden = disable_raw_creds || localLoginDisabledByProvider;

  // Countdown timer effect
  useEffect(() => {
    if (countdown <= 0) {
      setCountdownMessage("");
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        const newValue = prev - 1;
        if (newValue <= 0) {
          setCountdownMessage("");
          props.setError({ ...props.error, show: false });
        }
        return newValue;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  const usernameIsValid = () => {
    // ensure username is emailaddy
    return !!user.username && validator.validate(user.username);
  };

  const passwordIsValid = () => {
    return user.password;
  };

  const formIsValid = () => {
    // ensure user and password fields validated
    return usernameIsValid() && passwordIsValid();
  };

  const handleResendValidation = async () => {
    if (!emailForResend || resendLoading) return;
    setResendLoading(true);
    try {
      await axios.post(`${nuclearn_api_url}/auth/validation-email/resend`, {
        username: emailForResend,
      });
      setResendSuccess(true);
    } catch (err: any) {
      if (err.response?.status === 429) {
        const retryAfterHeader = err.response.headers?.["retry-after"];
        const retryAfter = retryAfterHeader
          ? parseInt(retryAfterHeader, 10)
          : null;
        if (retryAfter) {
          setCountdown(retryAfter);
          setCountdownMessage("Too many attempts. Please wait ");
        }
        props.setError({
          message: "Too many requests. Please try again later.",
          error: err,
          show: true,
        });
      } else {
        props.setError({
          message: "Failed to resend verification email. Please try again.",
          error: err,
          show: true,
        });
      }
    } finally {
      setResendLoading(false);
    }
  };

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    // submits the form field values to the login route, and set session with the received jwt
    event.preventDefault();
    axios
      .post(`${nuclearn_api_url}/auth/login`, {
        username: user.username,
        password: user.password,
      })
      .then((response) => {
        // don't rerender state here without user details being set first in browser storage
        if (nuclearn_user_token) {
          localStorage.setItem(
            nuclearn_user_token,
            response.data.token_type + " " + response.data.access_token,
          );
        }

        // Regenerate CSRF token after successful login to prevent token fixation
        // See: https://github.com/NuclearnAI/frontend/issues/719
        regenerateCSRFToken().catch((err) => {
          console.error("Failed to regenerate CSRF token:", err);
        });

        getUserFromToken()
          .then(async (userdata) => {
            localStorage.setItem("nuclearn_user", JSON.stringify(userdata));
            setPostHogUser();

            // set the logged in user sessionStorage
            props.setAuthorized(isAuthorizedUser());
          })
          .catch((error) => {
            console.error("Error fetching user data:", error);
            props.setError({
              message: "Failed to fetch user data after retrieving token",
              show: true,
              error: error,
            });
          });
      })
      .catch((error) => {
        if (error.response) {
          const status = error.response.status;
          const retryAfterHeader = error.response.headers?.["retry-after"];
          const retryAfter = retryAfterHeader
            ? parseInt(retryAfterHeader, 10)
            : null;

          if (status === 429) {
            // Rate limited
            if (retryAfter) {
              setCountdown(retryAfter);
              setCountdownMessage("Too many attempts. Please wait ");
            }
            props.setError({
              message: retryAfter
                ? "Too many login attempts."
                : "Too many login attempts. Please try again later.",
              error: error,
              show: true,
            });
          } else if (
            status === 403 &&
            // The axios interceptor normalizes the structured {message, code}
            // detail to its message string and moves the code to detail_code.
            error.response.data?.detail_code === "EMAIL_NOT_VALIDATED"
          ) {
            // Email not validated — freeze email for resend and show option
            setEmailForResend(user.username);
            setResendSuccess(false);
            props.setError({
              message: error.response.data.detail,
              error: error,
              show: true,
            });
          } else if (
            status === 403 &&
            typeof error.response.data?.detail === "string" &&
            error.response.data.detail.toLowerCase().includes("locked")
          ) {
            // Account locked
            if (retryAfter) {
              setCountdown(retryAfter);
              setCountdownMessage("Account locked. Please wait ");
            }
            props.setError({
              message: retryAfter
                ? "Account is locked due to too many failed attempts."
                : error.response.data.detail,
              error: error,
              show: true,
            });
          } else if (status === 401 || status === 403) {
            // Invalid credentials
            props.setError({
              message: extractErrorDetail(
                error,
                "Login failed. Please try again.",
              ),
              error: error,
              show: true,
            });
          } else {
            props.setError({
              message: extractErrorDetail(
                error,
                "Login failed. Please try again.",
              ),
              error: error,
              show: true,
            });
          }
        } else {
          props.setError({
            message: `Cannot reach ${determineLoginPlatformType()} API. Contact ${determineLoginPlatformType()} Support if error persists.`,
            show: true,
            error: "",
          });
        }
      });
  }

  const handleFormChange = (event: React.FormEvent<HTMLInputElement>) => {
    // handle any changes to the underlying form as fields are filled
    const { name, value } = event.target as HTMLInputElement;

    setUser({ ...user, [name]: value });
    props.setError({ ...props.error, show: false });
    setEmailForResend(null);
    setResendSuccess(false);
  };

  useEffect(() => {
    const providerId = sessionStorage.getItem("oauth_provider_id");
    const searchParams = new URLSearchParams(window.location.search);
    // get error from url params
    const error = searchParams.get("error");
    const error_description = searchParams.get("error_description");
    if (error) {
      // If error_description is present, this is a raw OAuth error from the IDP
      // If not, this is a user-friendly message already processed by our handler
      const message = error_description
        ? `Error from OAuth provider: ${error} - ${error_description}`
        : decodeURIComponent(error);
      props.setError({
        message,
        show: true,
        error: "",
      });
      // Clear the error from URL to prevent showing it again on refresh
      window.history.replaceState({}, "", window.location.pathname);
    }

    if (!providerId || !oauthProviders) {
      return;
    }

    const matchedProvider = Object.values(oauthProviders).find(
      (p) => String(p.id) === providerId,
    );

    if (!matchedProvider) {
      console.error(`OAuth provider with id "${providerId}" not found.`);
      return;
    }

    // create the provider (enriched so the callback's OAuthService
    // carries the same disablePKCE + MRE token_endpoint config)
    const oauthProvider = OAuthService.getInstance(
      buildOAuthProvider(matchedProvider),
    );

    // get the user from the provider
    oauthProvider.handleCallback(history).catch((error) => {
      console.error("OAuth callback error:", error);
    });
  }, [oauthProviders]);
  return (
    <>
      {/* Loader */}
      <div
        className="text-center"
        hidden={!window.location.search.includes("code")}
      >
        <LinearProgress />
      </div>
      <CForm
        onSubmit={handleSubmit}
        hidden={
          // url has code in query params
          window.location.search.includes("code")
        }
      >
        <CInputGroup className="mb-3" hidden={passwordLoginHidden}>
          <CInputGroupPrepend>
            <CInputGroupText>@</CInputGroupText>
          </CInputGroupPrepend>
          <CInput
            type="email"
            placeholder="Email"
            autoComplete="username"
            onChange={handleFormChange}
            name="username"
            minLength={EMAIL_MIN_LENGTH}
            maxLength={EMAIL_MAX_LENGTH}
            valid={usernameIsValid()}
            invalid={!usernameIsValid() && !!user.username}
            data-testid="login-email-input"
            data-attr="auth:input:email"
          />
        </CInputGroup>
        <CInputGroup className="mb-3" hidden={passwordLoginHidden}>
          <CInputGroupPrepend>
            <CInputGroupText>
              <CIcon name="cil-lock-locked" />
            </CInputGroupText>
          </CInputGroupPrepend>
          <CInput
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            autoComplete="current-password"
            onChange={handleFormChange}
            name="password"
            data-testid="login-password-input"
            data-attr="auth:input:password"
          />
          <CInputGroupAppend>
            <CInputGroupText
              role="button"
              tabIndex={0}
              style={{ cursor: "pointer" }}
              onClick={() => setShowPassword((show) => !show)}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setShowPassword((show) => !show);
                }
              }}
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
              data-testid="login-password-visibility-toggle"
              data-attr="auth:btn:toggle-password-visibility"
            >
              {showPassword ? (
                <VisibilityOffIcon fontSize="inherit" />
              ) : (
                <VisibilityIcon fontSize="inherit" />
              )}
            </CInputGroupText>
          </CInputGroupAppend>
        </CInputGroup>
        {!passwordLoginHidden && (
          <LoginButton
            disabled={!formIsValid()}
            countdown={countdown}
          ></LoginButton>
        )}
        {localLoginDisabledByProvider && (
          <CAlert color="info" style={{ marginTop: "8px" }}>
            Password login is disabled for this deployment. Sign in using your
            identity provider below.
          </CAlert>
        )}
        <ProviderButtons />
        {countdown > 0 && countdownMessage && (
          <div
            style={{ textAlign: "center", marginTop: "8px", color: "#856404" }}
          >
            {countdownMessage}
            {formatCountdown(countdown)}.
          </div>
        )}
        {emailForResend && !resendSuccess && (
          <div style={{ textAlign: "center", marginTop: "8px" }}>
            <CButton
              color="link"
              disabled={resendLoading || countdown > 0}
              onClick={handleResendValidation}
              style={{ padding: 0 }}
              data-attr="auth:btn:resend-verification"
            >
              {resendLoading ? "Sending..." : "Resend verification email"}
            </CButton>
          </div>
        )}
        {resendSuccess && (
          <CAlert
            color="success"
            style={{
              textAlign: "center",
              marginTop: "8px",
              marginBottom: "0px",
            }}
          >
            Verification email sent to <b>{emailForResend}</b>. Check your
            inbox.
          </CAlert>
        )}
        <CInputGroup className="mb-3"></CInputGroup>
        {!passwordLoginHidden && (
          <RegisterNav allowUserCreation={props.allowUserCreation} />
        )}
      </CForm>
    </>
  );
};

interface LoginProviderProps {
  setAuthorized: (isAuthorized: boolean) => void;
  error: { show: boolean; message: string; error: string };
  setError: (error: { show: boolean; message: string; error: string }) => void;
}

const LoginProvider = ({
  setAuthorized,
  error,
  setError,
}: LoginProviderProps) => {
  const { data: authConfig, isSuccess, isLoading, isError } = useAuthConfig();
  const platformType = determineLoginPlatformType();

  /*
   * Fix for loading API issues: Handle loading state explicitly to prevent
   * premature error display while the auth config API is still loading.
   * Previously, the switch statement would fall through to default case
   * during loading state, showing error before API call completed.
   */

  // Show loading spinner while API is loading
  if (isLoading) {
    return (
      <div className="text-center">
        <LinearProgress />
        <p className="mt-2">Loading authentication configuration...</p>
      </div>
    );
  }

  // Only show error if there's actually an error or if loading completed but no auth method found
  if (isError || (isSuccess && !authConfig?.authMethod)) {
    return (
      <CAlert style={{ textAlign: "center" }} color="danger">
        <b style={{ marginBottom: "1rem", display: "block" }}>
          {` The ${platformType} web interface cannot load an Identity Provider from the  
          ${platformType} API.`}
        </b>
        {`This is due to the ${platformType} API not operating or network issues
        preventing the ${platformType} web interface from reaching the API. Check
        that the ${platformType} API is running and that the ${platformType} API can be
        reached or contact ${platformType} technical support.`}
      </CAlert>
    );
  }

  switch (authConfig?.authMethod) {
    case "AzureAD":
      // msal-browser needs Web Crypto, which only exists in secure contexts
      // (https / localhost). Mounting it over plain http crashes the redirect
      // flow with no UI feedback (FRONTEND-C6), so block with an explanation
      // instead.
      if (!window.isSecureContext) {
        return (
          <CAlert
            style={{ textAlign: "center" }}
            color="danger"
            data-testid="login-https-required-alert"
          >
            <b style={{ marginBottom: "1rem", display: "block" }}>
              Secure (HTTPS) connection required
            </b>
            {`Microsoft sign-in cannot run over an insecure (http) connection.
            Re-open this site using its https:// address, or contact your
            administrator if the secure address is unavailable.`}
          </CAlert>
        );
      }
      return (
        <WrappedAzureADLogin
          setError={setError}
          onAuthorized={() => {
            UserService.setUserDataFromUserAuthZToken(async () => {
              setAuthorized(isAuthorizedUser());
            });
          }}
        />
      );

    case "ActiveDirectory":
      return (
        <RawCredsLogin
          setAuthorized={setAuthorized}
          error={error}
          setError={setError}
          allowUserCreation={false}
        />
      );
    case "ApplicationDatabase":
      return (
        <RawCredsLogin
          setAuthorized={setAuthorized}
          error={error}
          setError={setError}
          allowUserCreation={true}
        />
      );
    default:
      // This case should not be reached due to the explicit error handling above
      return (
        <CAlert style={{ textAlign: "center" }} color="danger">
          <b>Unknown authentication method: {authConfig?.authMethod}</b>
        </CAlert>
      );
  }
};

export const LoginPage = () => {
  const [isAuthorized, setAuthorized] = useState<boolean>(false);
  const [error, setError] = useState({ show: false, message: "", error: "" });
  const history = useHistory();
  const location = useLocation();

  // Get the 'from' location from sessionStorage or default to "/"
  const from = sessionStorage.getItem("redirectUrl") || "/";

  useEffect(() => {
    if (isAuthorized) {
      sessionStorage.removeItem("redirectUrl");
      history.push(from);
    }
  }, [isAuthorized, history, from]);

  // OAuth callback failures redirect here with the failure reason in a
  // ``?error=...`` query param (see ``handleCallback`` in
  // helpers/OAuth/oauth.tsx). Lift that into the page's error state so
  // the LoginError banner renders it, then strip the param so a refresh
  // doesn't re-show the stale message.
  //
  // Depends on ``location.search`` so it picks up post-mount URL
  // changes — the OAuth callback uses ``history.replace`` to write
  // the error AFTER the login page has already mounted, so a
  // mount-only effect would miss it.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const errorParam = params.get("error");
    if (!errorParam) return;
    setError({ show: true, message: errorParam, error: "oauth_callback" });
    params.delete("error");
    const cleanedSearch = params.toString();
    history.replace({
      pathname: location.pathname,
      search: cleanedSearch ? `?${cleanedSearch}` : "",
    });
  }, [location.search, location.pathname, history]);

  return (
    <div className="login-background">
      <div
        style={{
          backgroundColor: "rgba(0, 0, 0, 0.3)",
          display: "flex",
          flexDirection: "row",
        }}
      >
        <div style={{ width: "100%" }}>
          <div
            className="c-app c-default-layout split-right flex-row align-items-center"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.0)" }}
          >
            <CContainer
              style={{
                backgroundColor: "transparent",
                borderColor: "transparent",
              }}
            >
              <CRow className="justify-content-center">
                <CCol xs="12" sm="10" md="6" lg="5" xl="4">
                  <CCardGroup>
                    <CCard className="p-4 auth-card">
                      <CCardBody>
                        <CImg
                          src={
                            isFastDraft()
                              ? "/images/product-logos/768x768 square fast draft.png"
                              : "/images/nuclearn-n-full.png"
                          }
                          className="mx-auto d-block login-img"
                          // height={100}
                          style={{ width: "auto", height: "8vh" }}
                        />
                        <LoginError show={error.show} message={error.message} />
                        <LoginProvider
                          setAuthorized={setAuthorized}
                          error={error}
                          setError={setError}
                        />
                      </CCardBody>
                    </CCard>
                  </CCardGroup>
                </CCol>
              </CRow>
            </CContainer>
          </div>
        </div>
      </div>
    </div>
  );
};
export function logoutCurrentUser(query_client: QueryClient) {
  // remove the invalid authz token from the cache
  console.log("Removing authZToken from cache");
  query_client.invalidateQueries("authZToken");

  console.log('Removing "nuclearn_user_token" from localStorage');
  // remove user from local storage to log user out
  nuclearn_user_token && localStorage.removeItem(nuclearn_user_token);
  localStorage.removeItem("nuclearn_user");

  // Clear per-user client state on EVERY logout path, not just the button
  // (6.6.16). Persisted stores keyed in sessionStorage (e.g. ChatStore's
  // `activeDialogueId`) would otherwise rehydrate for the next user on a
  // same-tab switch / token-expiry, leaking the prior user's resumed chat.
  sessionStorage.clear();
  // In-memory per-user stores don't live in storage; wipe them too so a
  // shared workstation's next user can't see the previous user's alerts.
  useUserNotificationStore.getState().reset();

  Sentry.setUser(null);
  resetPostHogUser();

  // Clear CSRF token on logout
  // See: https://github.com/NuclearnAI/frontend/issues/719
  clearCSRFToken();
}

interface LogOutComponentProps {
  clicked: boolean;
}

export function LogOutComponent({ clicked }: LogOutComponentProps) {
  const queryClient = useQueryClient();
  const [isAuthorized, setIsAuthorized] = useState<boolean>(isAuthorizedUser());
  const { data: authConfig, isSuccess } = useAuthConfig();
  const history = useHistory();

  const authMethod = authConfig?.authMethod;

  const handleLogout = () => {
    switch (isSuccess && authMethod) {
      case "AzureAD":
        useMSALInstance().then((msalInstance) => {
          console.log("Logging out of AzureAD");
          if (msalInstance) msalInstance.logout();
          logoutCurrentUser(queryClient);
          sessionStorage.clear();
          localStorage.clear();
          setIsAuthorized(false);
        });
        break;

      default:
        logoutCurrentUser(queryClient);
        sessionStorage.clear();
        localStorage.clear();
        setIsAuthorized(false);
        break;
    }
    console.log('Logoff complete, redirecting to "/"');
  };

  useEffect(() => {
    clicked && handleLogout();
  }, [clicked]);

  useEffect(() => {
    if (!isAuthorized) {
      history.push("/");
    }
  }, [isAuthorized, history]);

  if (!isAuthorized) {
    return null;
  }

  return (
    <>
      <div
        onClick={handleLogout}
        className="mr-1 p-0 "
        style={{ color: "#4f5d73" }}
        data-attr="auth:btn:logout"
      >
        Log Off
      </div>
    </>
  );
}

export default LoginPage;
